import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Inbox as InboxIcon, MessageSquare, Sparkles, Users2, CheckCircle2, XCircle,
  ArrowRight, Phone, Mail, Ban, Send, ShieldCheck, UserCog, User, UserPlus,
} from "lucide-react";
import { sr } from "../../lib/sr";

const STATUS_BADGE = { new: "sr-badge-amber", contacted: "sr-badge", won: "sr-badge-green", lost: "sr-badge-gray" };
const CHANNEL_ICON = { sms: Phone, phone: Phone, voice: Phone, email: Mail, web: MessageSquare, manual: User };

function LeadCard({ lead, busy, onStatus, onReply, onOptOut }) {
  const [reply, setReply] = useState("");
  const [showReply, setShowReply] = useState(false);
  const Ch = CHANNEL_ICON[lead.channel] || MessageSquare;
  return (
    <div className="sr-card" data-testid={`inbox-lead-${lead.id}`} style={{ display: "grid", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Ch size={16} style={{ color: "var(--blue)", flexShrink: 0 }} />
        <strong style={{ color: "var(--navy)" }}>{lead.name || lead.phone || lead.email || "New lead"}</strong>
        <span className={`sr-badge ${STATUS_BADGE[lead.status] || "sr-badge-gray"}`} data-testid={`inbox-lead-status-${lead.id}`}>{lead.status || "new"}</span>
        {lead.channel && <span className="sr-badge sr-badge-gray" style={{ fontSize: 10 }}>{lead.channel}</span>}
      </div>
      {lead.message && <p className="sr-muted" style={{ fontSize: 13, margin: 0 }}>“{lead.message}”</p>}
      <div className="sr-muted" style={{ fontSize: 12, display: "flex", gap: 12, flexWrap: "wrap" }}>
        {lead.phone && <span>{lead.phone}</span>}
        {lead.email && <span>{lead.email}</span>}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {lead.status !== "contacted" && <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={busy} onClick={() => onStatus(lead.id, "contacted")} data-testid={`inbox-lead-contacted-${lead.id}`}>Mark contacted</button>}
        {lead.status !== "won" && <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={busy} onClick={() => onStatus(lead.id, "won")} data-testid={`inbox-lead-won-${lead.id}`}>Won</button>}
        {lead.status !== "lost" && <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={busy} onClick={() => onStatus(lead.id, "lost")} data-testid={`inbox-lead-lost-${lead.id}`}>Lost</button>}
        {lead.phone && <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={busy} onClick={() => setShowReply((s) => !s)} data-testid={`inbox-lead-reply-toggle-${lead.id}`}><MessageSquare size={13} /> Reply</button>}
        <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={busy} onClick={() => onOptOut(lead.id)} data-testid={`inbox-lead-optout-${lead.id}`} style={{ color: "#C0362C" }}><Ban size={13} /> Opt out</button>
      </div>
      {showReply && (
        <div style={{ display: "flex", gap: 8 }}>
          <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Text a quick reply…" className="sr-input" style={{ flex: 1 }} data-testid={`inbox-lead-reply-input-${lead.id}`} />
          <button className="sr-btn sr-btn-primary sr-btn-sm" disabled={busy || !reply.trim()} data-testid={`inbox-lead-reply-send-${lead.id}`}
            onClick={async () => { await onReply(lead.id, reply.trim()); setReply(""); setShowReply(false); }}><Send size={13} /> Send</button>
        </div>
      )}
    </div>
  );
}

function ApprovalCard({ item, busy, onRec, onCampaign }) {
  const isRec = item.kind === "recommendation";
  return (
    <div className="sr-card" data-testid={`inbox-approval-${item.id}`} style={{ display: "grid", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {isRec ? <Sparkles size={15} style={{ color: "var(--gold)" }} /> : <ShieldCheck size={15} style={{ color: "var(--blue)" }} />}
        <span className="sr-badge">{isRec ? (item.agent_type || "AI recommendation") : "Campaign sign-off"}</span>
        {isRec && item.confidence != null && <span className="sr-badge sr-badge-gray">Confidence: {item.confidence}</span>}
        {!isRec && item.version != null && <span className="sr-badge sr-badge-gray">v{item.version}</span>}
      </div>
      <div>
        <strong style={{ color: "var(--navy)", fontSize: 15 }}>{item.title}</strong>
        {item.summary && <p className="sr-muted" style={{ fontSize: 13, marginTop: 4 }}>{item.summary}</p>}
        {isRec && item.expected_impact?.metric && (
          <p style={{ fontSize: 12, marginTop: 6, color: "var(--navy)", fontWeight: 600 }}>
            Expected: {item.expected_impact.metric}
            {item.expected_impact.low != null && ` ${item.expected_impact.low}–${item.expected_impact.high}${item.expected_impact.unit || ""}`}
          </p>
        )}
        {!isRec && item.submitted_by_email && <p className="sr-muted" style={{ fontSize: 12, marginTop: 4 }}>Submitted by {item.submitted_by_email}</p>}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button className="sr-btn sr-btn-primary sr-btn-sm" disabled={busy}
          onClick={() => (isRec ? onRec(item.id, true) : onCampaign(item.id, "approve"))}
          data-testid={`inbox-approve-${item.id}`}><CheckCircle2 size={14} /> Approve</button>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={busy}
          onClick={() => (isRec ? onRec(item.id, false) : onCampaign(item.id, "reject"))}
          data-testid={`inbox-reject-${item.id}`}><XCircle size={14} /> {isRec ? "Dismiss" : "Reject"}</button>
        {!isRec && item.campaign_id && (
          <Link to={`/app/campaigns/${item.campaign_id}/project`} className="sr-btn sr-btn-ghost sr-btn-sm" data-testid={`inbox-open-campaign-${item.id}`}>Open <ArrowRight size={13} /></Link>
        )}
      </div>
    </div>
  );
}

const TEAM_ICON = { owner: UserCog, manager: ShieldCheck, invited: Mail };

export default function Inbox() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("leads");
  const [busy, setBusy] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");

  const load = () => sr.inbox().then(setData).catch(() => setData({ error: true }));
  useEffect(() => { load(); }, []);

  const wrap = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };
  const setStatus = (id, status) => wrap(() => sr.updateLeadStatus(id, status), `Marked ${status}`);
  const optOut = (id) => wrap(() => sr.optOutLead(id), "Contact opted out");
  const reply = (id, text) => wrap(() => sr.replyLead(id, text), "Reply sent");
  const decideRec = (id, approve) => wrap(() => (approve ? sr.approveRec(id) : sr.rejectRec(id)), approve ? "Approved" : "Dismissed");
  const decideCampaign = (id, decision) => wrap(() => sr.decideApproval(id, { decision, comment: "" }), decision === "approve" ? "Approved" : "Rejected");
  const sendInvite = () => {
    const email = inviteEmail.trim();
    if (!email) { toast.error("Enter an email address"); return; }
    wrap(async () => {
      const r = await sr.inboxTeamInvite(email);
      setInviteEmail("");
      toast.success(r.emailed
        ? `Invite emailed to ${r.email}`
        : `${r.email} added to your workspace${r.temp_password ? ` · temp password ${r.temp_password}` : ""}`);
    });
  };

  if (!data) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  if (data.error) return <div className="sr-card"><p className="sr-muted">Could not load your inbox. Please try again.</p></div>;

  const c = data.counts || {};
  const TABS = [
    { key: "leads", label: "Leads", icon: MessageSquare, count: c.leads },
    { key: "approvals", label: "Approvals", icon: Sparkles, count: c.approvals },
    { key: "team", label: "Team", icon: Users2, count: c.team },
  ];

  return (
    <div data-testid="inbox-page">
      <div style={{ marginBottom: 6 }}>
        <span className="sr-badge" style={{ marginBottom: 8 }}><InboxIcon size={12} /> Inbox</span>
        <h1 style={{ fontSize: 30 }}>Your inbox</h1>
        <p className="sr-muted" style={{ marginTop: 6 }}>Everything that needs you in one place — new leads, pending approvals, and your team.</p>
      </div>

      <div style={{ display: "flex", gap: 6, borderBottom: "1px solid var(--border)", margin: "18px 0 18px" }} data-testid="inbox-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`inbox-tab-${t.key}`}
            style={{ display: "flex", alignItems: "center", gap: 7, background: "none", border: "none", cursor: "pointer",
              padding: "10px 14px", fontSize: 14, fontWeight: 700, color: tab === t.key ? "var(--navy)" : "var(--muted)",
              borderBottom: `2px solid ${tab === t.key ? "var(--blue)" : "transparent"}`, marginBottom: -1 }}>
            <t.icon size={15} /> {t.label}
            {t.count > 0 && <span className="sr-badge sr-badge-amber" data-testid={`inbox-count-${t.key}`} style={{ fontSize: 10 }}>{t.count}</span>}
          </button>
        ))}
      </div>

      {tab === "leads" && (
        <div style={{ display: "grid", gap: 12 }} data-testid="inbox-leads">
          {data.leads.length === 0 ? (
            <div className="sr-card sr-empty" data-testid="inbox-leads-empty">
              <MessageSquare size={24} style={{ opacity: .4 }} />
              <p style={{ marginTop: 10 }}>No open leads right now. New leads from your website, SMS, calls and email land here.</p>
              <Link to="/app/leads" className="sr-btn sr-btn-ghost sr-btn-sm" style={{ marginTop: 12 }}>Set up Lead Concierge <ArrowRight size={13} /></Link>
            </div>
          ) : (
            <>
              {data.leads.map((l) => <LeadCard key={l.id} lead={l} busy={busy} onStatus={setStatus} onReply={reply} onOptOut={optOut} />)}
              <Link to="/app/leads" className="sr-btn sr-btn-ghost sr-btn-sm" style={{ justifySelf: "start" }} data-testid="inbox-all-leads">Open full Lead Concierge <ArrowRight size={13} /></Link>
            </>
          )}
        </div>
      )}

      {tab === "approvals" && (
        <div style={{ display: "grid", gap: 12 }} data-testid="inbox-approvals">
          {data.approvals.length === 0 ? (
            <div className="sr-card sr-empty" data-testid="inbox-approvals-empty">
              <CheckCircle2 size={24} style={{ opacity: .4 }} />
              <p style={{ marginTop: 10 }}>You're all caught up — nothing waiting on your approval.</p>
            </div>
          ) : data.approvals.map((a) => <ApprovalCard key={a.id} item={a} busy={busy} onRec={decideRec} onCampaign={decideCampaign} />)}
        </div>
      )}

      {tab === "team" && (
        <div style={{ display: "grid", gap: 12 }} data-testid="inbox-team">
          {data.team.length === 0 ? (
            <div className="sr-card sr-empty" data-testid="inbox-team-empty"><p>No teammates on this workspace yet.</p></div>
          ) : data.team.map((m, i) => {
            const Ico = TEAM_ICON[m.kind] || User;
            return (
              <div key={i} className="sr-card" data-testid={`inbox-team-${m.kind}`} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span className="sr-pill-ico" style={{ background: "var(--blue-light)", color: "var(--blue)" }}><Ico size={18} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "var(--navy)", fontWeight: 700 }}>{m.name || m.email || "Teammate"}</div>
                  {m.email && m.name && <div className="sr-muted" style={{ fontSize: 12 }}>{m.email}</div>}
                </div>
                <span className="sr-badge sr-badge-gray">{m.role}</span>
              </div>
            );
          })}
          <div className="sr-card" data-testid="inbox-invite">
            <div style={{ color: "var(--navy)", fontWeight: 700, marginBottom: 4 }}>Invite a teammate</div>
            <p className="sr-muted" style={{ fontSize: 12, marginTop: 0, marginBottom: 10 }}>Add someone to help run this workspace — they'll get their own sign-in.</p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input className="sr-input" type="email" placeholder="teammate@email.com" value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)} data-testid="invite-email-input" style={{ maxWidth: 260 }} />
              <button className="sr-btn sr-btn-primary sr-btn-sm" disabled={busy} onClick={sendInvite} data-testid="invite-send-btn"><UserPlus size={14} /> Send invite</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
